import { Divider, type DividerProps } from 'antd';
import { cn } from '@/common/utils';
import { Slot } from '../../Slot';
import Tooltip, { type TooltipProps } from '../../Tooltip';
import { useDashboardSidebarCtx } from '../context';
import { sidebarRoutesCtx } from './context';
import type { SidebarRoutesContextProps } from './types';

type Ul = JSX.IntrinsicElements['ul'];
type Li = JSX.IntrinsicElements['li'];
type Div = JSX.IntrinsicElements['div'];
type Paragraph = JSX.IntrinsicElements['p'];
type Anchor = JSX.IntrinsicElements['a'];
type Span = JSX.IntrinsicElements['span'];

const RoutesRoot = ({
  className,
  value,
  ...props
}: Ul & {
  value: SidebarRoutesContextProps;
}) => {
  return (
    <sidebarRoutesCtx.Provider value={value}>
      <ul
        className={cn(
          'flex flex-col gap-4 px-1 overflow-y-auto mb-4 w-full',
          className,
        )}
        {...props}
      ></ul>
    </sidebarRoutesCtx.Provider>
  );
};

const RoutesSectionContainer = (props: Div) => {
  return <div {...props}></div>;
};

const RoutesSectionTitle = (props: Paragraph) => {
  const { sidebarExpanded } = useDashboardSidebarCtx();
  return (
    <p
      role="heading"
      className={cn(
        'flex pb-[5.5px] pt-[4.5px] !text-sh10 font-medium text-neutral-600 transition duration-500',
        sidebarExpanded ? 'w-full' : '-translate-x-[200px] h-0',
      )}
      {...props}
    ></p>
  );
};

const RoutesSectionDivider = ({ className, ...props }: DividerProps) => {
  const { sidebarExpanded } = useDashboardSidebarCtx();
  return (
    <Divider
      className={cn(
        'transition-all duration-500',
        sidebarExpanded
          ? 'm-0 w-full border-transparent -translate-x-[200px]'
          : 'mt-3 mb-6 mr-0 w-4 min-w-0 -ml-0.5 border-neutral-300',
        className,
      )}
      {...props}
    />
  );
};

const RoutesList = ({ className, ...props }: Div) => {
  const { sidebarExpanded } = useDashboardSidebarCtx();

  return (
    <div
      className={cn(
        'flex flex-col transition-[gap] duration-500',
        !sidebarExpanded && 'gap-1',
      )}
      {...props}
    ></div>
  );
};

const RoutesListItem = ({
  className,
  isCollapsible = false,
  isCollapsibleChildItem = false,
  ...props
}: Li & {
  isCollapsible?: boolean;
  isCollapsibleChildItem?: boolean;
}) => {
  return (
    <li
      className={cn(
        'flex relative py-[5px]',
        isCollapsible
          ? 'flex-col items-start justify-center'
          : 'flex items-center justify-between',
        isCollapsibleChildItem ? 'ml-5 py-0' : '',
        className,
      )}
      {...props}
    ></li>
  );
};

const RoutesListItemTooltip = ({
  className,
  children,
  ...props
}: TooltipProps & { label: string }) => {
  const { sidebarExpanded } = useDashboardSidebarCtx();
  const iconOnly = !sidebarExpanded;
  return (
    <Tooltip
      overlayInnerStyle={{
        minHeight: '24px',
      }}
      type="primary"
      title={<span className="font-medium">{props.label}</span>}
      placement="right"
      trigger={iconOnly ? 'hover' : []}
      open={iconOnly ? undefined : false}
      {...props}
    >
      {children}
    </Tooltip>
  );
};

const RoutesListItemLink = ({
  className,
  asChild,
  ...props
}: Anchor & { asChild?: boolean }) => {
  const Comp = asChild ? Slot : 'a';
  return (
    <Comp
      className={cn('flex items-start space-x-2', className)}
      {...props}
    ></Comp>
  );
};

const RoutesIconRoot = ({ className, ...props }: Div) => {
  const { sidebarExpanded } = useDashboardSidebarCtx();

  return (
    <div
      className={cn(
        'flex transition-transform duration-500',
        !sidebarExpanded && 'scale-[135%]',
      )}
      {...props}
    ></div>
  );
};

const RoutesListItemText = ({
  className,
  isActive,
  asChild,
  ...props
}: Span & { asChild?: boolean; isActive: boolean }) => {
  const Comp = asChild ? Slot : 'span';
  const { sidebarExpanded } = useDashboardSidebarCtx();
  const iconOnly = !sidebarExpanded;
  return (
    <Comp
      className={cn(
        'flex items-start space-x-2',
        'text-left !text-sh9 opacity-100 font-medium text-neutral-1000',
        isActive && 'font-bold text-violet-500',
        iconOnly && 'hidden',
        className,
      )}
      {...props}
    ></Comp>
  );
};

const RoutesCollapsibleRoot = ({
  className,
  open,
  ...props
}: Ul & { open: boolean }) => {
  const { sidebarExpanded } = useDashboardSidebarCtx();
  const iconOnly = !sidebarExpanded;
  return (
    <ul
      className={cn(
        'overflow-hidden transition-all duration-500 ease-in-out space-y-2.5',
        iconOnly && 'hidden',
        open ? 'visible h-auto pt-2.5' : 'invisible h-0 opacity-0',
      )}
      {...props}
    ></ul>
  );
};

export {
  RoutesRoot,
  RoutesSectionContainer,
  RoutesSectionDivider,
  RoutesSectionTitle,
  RoutesList,
  RoutesListItem,
  RoutesListItemTooltip,
  RoutesListItemLink,
  RoutesListItemText,
  RoutesCollapsibleRoot,
  RoutesIconRoot,
};
